import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import styles from './AppBar.module.css';
import { navigationConfig } from '../../config/titleConfig';
import { useLanguage } from '../../context/LanguageContext';
import LanguageSelector from '../LanguageSelector/LanguageSelector';

interface AppBarProps {
  isAuthenticated: boolean;
  onLogout: () => void;
  onToggleSidebar: () => void;
  isSidebarOpen: boolean;
}

export default function AppBar({ 
  onLogout, 
  onToggleSidebar, 
  isSidebarOpen 
}: AppBarProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { t } = useLanguage();

  const getCurrentPage = () => {
    const exactMatch = navigationConfig.find(page => page.path === location.pathname);
    if (exactMatch) return exactMatch;

    return navigationConfig.find(page => 
      page.pattern && page.pattern.test(location.pathname)
    );
  };

  const getPageTitle = (): string => {
    const current = getCurrentPage();
    return current ? t(current.title) : t('dynamicFormBuilder');
  };

  const handleLogout = (): void => {
    onLogout();
    navigate('/');
    setIsMenuOpen(false);
  };

  const handleNavigate = (path: string): void => {
    navigate(path);
    setIsMenuOpen(false);
  };

  const toggleMenu = (): void => {
    setIsMenuOpen(!isMenuOpen);
  };

  return (
    <div className={styles.appBar}>
      <div className={styles.container}>
        <button
          className={`${styles.sidebarToggle} ${isSidebarOpen ? styles.sidebarOpen : ''}`}
          onClick={onToggleSidebar}
          aria-label={t('toggleSidebar')}
        >
          <svg
            className={styles.sidebarArrow}
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <polyline points="9 18 15 12 9 6" />
          </svg>
        </button>

        <div className={styles.logo}>
          <span>{getPageTitle()}</span>
        </div>

        <nav className={styles.navigation}>
          {navigationConfig
            .filter(page => !page.pattern) 
            .map(page => (
              <button
                key={page.path}
                className={`${styles.navButton} ${location.pathname === page.path ? styles.active : ''}`}
                onClick={() => navigate(page.path)}
              >
                {t(page.label)}
              </button>
            ))}
        </nav>

        <div className={styles.actions}>
          <LanguageSelector />

          <button className={styles.logoutButton} onClick={handleLogout}>
            {t('logout')}
          </button>
        </div>

        <button 
          className={`${styles.burgerButton} ${isMenuOpen ? styles.burgerOpen : ''}`}
          onClick={toggleMenu}
          aria-label={t('toggleMenu')}
        >
          <div className={styles.burgerIcon}>
            <span></span>
            <span></span>
            <span></span>
          </div>
        </button>
      </div>

      <div className={`${styles.mobileMenu} ${isMenuOpen ? styles.menuOpen : ''}`}>
        <div className={styles.menuHeader}>
          <span>{t('menu')}</span>
        </div>
        <nav className={styles.mobileNavigation}>
          {navigationConfig
            .filter(page => !page.pattern) 
            .map((page, index) => (
              <button
                key={page.path}
                className={`${styles.mobileNavButton} ${location.pathname === page.path ? styles.mobileActive : ''}`}
                onClick={() => handleNavigate(page.path)}
                style={{ transitionDelay: `${isMenuOpen ? index * 0.05 : 0}s` }}
              >
                {t(page.label)}
              </button>
            ))}
          <LanguageSelector />
          
          <div className={styles.divider} />

          <button
            className={styles.mobileLogoutButton}
            onClick={handleLogout}
            style={{ transitionDelay: `${isMenuOpen ? navigationConfig.length * 0.05 : 0}s` }}
          >
            {t('logout')}
          </button>
        </nav>
      </div>
    </div>
  );
}